import { Copy, MailPlus, SearchX } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import {
  invitationExpiryOptions,
  invitationListLimit,
  type CreatedInvitation,
  type Invitation,
  type InvitationStatus
} from "../../shared/invitations";
import { api, type Session } from "./api";
import { demoInvitations } from "./demo";
import {
  Empty,
  fmtDate,
  fmtInt,
  fmtRelative,
  Notice,
  PageHead,
  Segmented,
  SkeletonRows,
  StatusDot,
  useIsNarrow
} from "./ui";

type Filter = "all" | InvitationStatus;

/** "today" / "3 days ago" / "Sep 3, 2026", for use mid-sentence. */
function relative(value: string) {
  const text = fmtRelative(value);
  return text === "Today" || text === "Yesterday" ? text.toLowerCase() : text;
}

const statusTone: Record<InvitationStatus, "ok" | "warn" | "bad" | "neutral"> = {
  pending: "warn",
  accepted: "ok",
  expired: "neutral",
  revoked: "bad"
};

export function InvitationsView({ session, demo }: { session: Session; demo: boolean }) {
  const narrow = useIsNarrow();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [filter, setFilter] = useState<Filter>("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [inviteOutcome, setInviteOutcome] = useState<InviteOutcome>();
  const [revokeFailure, setRevokeFailure] = useState<string>();

  // `quiet` refreshes in place, without swapping the table for skeleton rows.
  const load = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!quiet) setLoading(true);
      setError(undefined);
      try {
        const data = demo
          ? { invitations: demoInvitations }
          : await api<{ invitations: Invitation[] }>("/api/invitations");
        setInvitations(data.invitations);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Invitations could not be loaded");
      } finally {
        setLoading(false);
      }
    },
    [demo]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const shown =
    filter === "all" ? invitations : invitations.filter((item) => item.status === filter);
  const count = (status: InvitationStatus) =>
    invitations.filter((item) => item.status === status).length;

  return (
    <section className="view">
      <PageHead
        title="Invitations"
        count={
          loading
            ? "Loading…"
            : `${fmtInt(shown.length)} shown${
                invitations.length >= invitationListLimit
                  ? ` · newest ${fmtInt(invitationListLimit)} only`
                  : ""
              }`
        }
        authority="Account platform"
      />

      <div className="toolbar">
        <InviteForm
          session={session}
          demo={demo}
          onOutcome={setInviteOutcome}
          onSent={(created) => {
            setInvitations((current) => [
              {
                id: created.id,
                email: created.email,
                status: "pending",
                invitedBy: session.user.email,
                createdAt: new Date().toISOString(),
                expiresAt: created.expiresAt,
                acceptedAt: null,
                revokedAt: null
              },
              ...current.map((item) =>
                (created.superseded ?? []).includes(item.id)
                  ? { ...item, status: "revoked" as const, revokedAt: new Date().toISOString() }
                  : item
              )
            ]);
            setFilter("pending");
          }}
        />
        <Segmented<Filter>
          label="Invitation status"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "pending", label: "Pending", count: count("pending") },
            { value: "accepted", label: "Accepted", count: count("accepted") },
            { value: "expired", label: "Expired", count: count("expired") },
            { value: "revoked", label: "Revoked", count: count("revoked") },
            { value: "all", label: "All", count: invitations.length }
          ]}
        />
      </div>

      {revokeFailure && (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Dismiss", onClick: () => setRevokeFailure(undefined) }}
        >
          {revokeFailure}
        </Notice>
      )}

      {inviteOutcome && (
        <InviteNotice
          key={"sent" in inviteOutcome ? inviteOutcome.sent.id : inviteOutcome.failure}
          outcome={inviteOutcome}
        />
      )}

      {error && (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Retry", onClick: () => void load() }}
        >
          {error}
        </Notice>
      )}

      <div className="table-wrap">
        <table className="records fixed">
          <colgroup>
            {narrow ? (
              <>
                <col style={{ width: "58%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "20%" }} />
              </>
            ) : (
              <>
                <col style={{ width: "30%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "10%" }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Status</th>
              {!narrow && <th scope="col">Invited by</th>}
              {!narrow && <th scope="col">Sent</th>}
              {!narrow && <th scope="col">Expires</th>}
              <th scope="col" className="right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows columns={narrow ? 3 : 6} rows={5} />
            ) : (
              shown.map((invitation) => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  narrow={narrow}
                  session={session}
                  demo={demo}
                  onRevokeFailed={(message) => {
                    setRevokeFailure(message);
                    // The invitation may have been accepted, expired, or
                    // revoked elsewhere; refresh in place so the notice stays.
                    void load({ quiet: true });
                  }}
                  onRevoked={(revokedAt) =>
                    setInvitations((current) =>
                      current.map((item) =>
                        item.id === invitation.id ? { ...item, status: "revoked", revokedAt } : item
                      )
                    )
                  }
                />
              ))
            )}
          </tbody>
        </table>
        {!loading && !shown.length && (
          <Empty
            icon={SearchX}
            title={filter === "all" ? "No invitations yet" : `No ${filter} invitations`}
          >
            Invite someone with the form above. They'll get an email with a link to create their
            account.
          </Empty>
        )}
      </div>
    </section>
  );
}

type InviteOutcome = { sent: CreatedInvitation } | { failure: string };

function InviteForm({
  session,
  demo,
  onSent,
  onOutcome
}: {
  session: Session;
  demo: boolean;
  onSent: (created: CreatedInvitation) => void;
  onOutcome: (outcome: InviteOutcome | undefined) => void;
}) {
  const [email, setEmail] = useState("");
  const [expiresInHours, setExpiresInHours] = useState<number>(168);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    onOutcome(undefined);
    try {
      const created = demo
        ? {
            id: crypto.randomUUID(),
            email: email.trim().toLowerCase(),
            expiresAt: new Date(Date.now() + expiresInHours * 3_600_000).toISOString(),
            actionUrl: "https://accounts.evidite.com/register?invitation=demo",
            superseded: []
          }
        : await api<CreatedInvitation>("/api/invitations", session, {
            method: "POST",
            body: JSON.stringify({ email: email.trim(), expiresInHours })
          });
      setEmail("");
      onSent(created);
      onOutcome({ sent: created });
    } catch (cause) {
      onOutcome({
        failure: cause instanceof Error ? cause.message : "The invitation could not be sent"
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="invite-form" onSubmit={(event) => void submit(event)}>
      <input
        type="email"
        required
        aria-label="Email address to invite"
        placeholder="name@example.com"
        autoComplete="off"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <select
        aria-label="Invitation expires after"
        value={expiresInHours}
        onChange={(event) => setExpiresInHours(Number(event.target.value))}
      >
        {invitationExpiryOptions.map((option) => (
          <option key={option.hours} value={option.hours}>
            {option.label}
          </option>
        ))}
      </select>
      <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>
        <MailPlus aria-hidden="true" /> {busy ? "Sending…" : "Send invitation"}
      </button>
    </form>
  );
}

const copyShortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘C" : "Ctrl+C";

function InviteNotice({ outcome }: { outcome: InviteOutcome }) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "manual">("idle");
  const linkRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (copyState !== "copied") return;
    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  if ("failure" in outcome)
    return (
      <Notice tone="error" className="toolbar-notice">
        {outcome.failure}
      </Notice>
    );
  const { sent } = outcome;
  const superseded = sent.superseded?.length ?? 0;

  function copy() {
    const fallback = () => {
      // No clipboard access (insecure origin, denied permission): select the
      // link so the user can copy it by hand.
      const node = linkRef.current;
      if (node) window.getSelection()?.selectAllChildren(node);
      setCopyState("manual");
    };
    if (!navigator.clipboard?.writeText) return fallback();
    navigator.clipboard.writeText(sent.actionUrl).then(() => setCopyState("copied"), fallback);
  }

  return (
    <Notice tone="success" className="toolbar-notice">
      Invitation emailed to <b>{sent.email}</b>
      {superseded > 0 &&
        ` (replacing ${superseded} earlier pending invitation${superseded === 1 ? "" : "s"})`}
      . If it doesn't arrive, share this one-time link directly — it only works for that address:
      <span className="invite-link">
        <code ref={linkRef}>{sent.actionUrl}</code>
        <button type="button" className="btn btn-quiet" onClick={copy}>
          <Copy aria-hidden="true" />{" "}
          {copyState === "copied"
            ? "Copied"
            : copyState === "manual"
              ? `Press ${copyShortcut}`
              : "Copy"}
        </button>
      </span>
    </Notice>
  );
}

function InvitationRow({
  invitation,
  narrow,
  session,
  demo,
  onRevoked,
  onRevokeFailed
}: {
  invitation: Invitation;
  narrow: boolean;
  session: Session;
  demo: boolean;
  onRevoked: (revokedAt: string) => void;
  onRevokeFailed: (message: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function revoke() {
    setBusy(true);
    try {
      const result = demo
        ? { revokedAt: new Date().toISOString() }
        : await api<{ revokedAt: string }>(
            `/api/invitations/${encodeURIComponent(invitation.id)}/revoke`,
            session,
            { method: "POST" }
          );
      onRevoked(result.revokedAt);
    } catch (cause) {
      setConfirming(false);
      const message =
        cause instanceof Error ? cause.message : "The invitation could not be revoked";
      onRevokeFailed(`Couldn't revoke the invitation for ${invitation.email}: ${message}`);
    } finally {
      setBusy(false);
    }
  }

  const when =
    invitation.status === "accepted" && invitation.acceptedAt
      ? `Accepted ${relative(invitation.acceptedAt)}`
      : invitation.status === "revoked" && invitation.revokedAt
        ? `Revoked ${relative(invitation.revokedAt)}`
        : fmtDate(invitation.expiresAt);

  return (
    <tr>
      <td className="cell-primary">
        <span className="cell-stack">
          <span className="truncate">{invitation.email}</span>
          {narrow && <small>{when}</small>}
        </span>
      </td>
      <td>
        <StatusDot value={invitation.status} tone={statusTone[invitation.status]} />
      </td>
      {!narrow && <td className="cell-muted truncate">{invitation.invitedBy ?? "—"}</td>}
      {!narrow && <td className="cell-faint">{fmtRelative(invitation.createdAt)}</td>}
      {!narrow && <td className="cell-faint">{when}</td>}
      <td className="right">
        {invitation.status === "pending" &&
          (confirming ? (
            <span className="cell-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setConfirming(false)}>
                Keep
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={busy}
                onClick={() => void revoke()}
              >
                {busy ? "Revoking…" : "Revoke"}
              </button>
            </span>
          ) : (
            <button type="button" className="btn btn-quiet" onClick={() => setConfirming(true)}>
              Revoke…
            </button>
          ))}
      </td>
    </tr>
  );
}
